import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { Copy, Save, Sheet, UserRound, UsersRound } from 'lucide-react';
import { desempenhoService } from '../services/desempenhoService';
import {
  CAMPOS_META_POR_TIPO,
  DesempenhoConfig,
  DesempenhoConfigFormData,
  DesempenhoFaixa,
  DesempenhoFaixaFormData,
  DesempenhoMembro,
  FAIXAS_DEFAULT_POR_TIPO,
  GrupoDesempenho,
  NIVEL_DESEMPENHO_LABELS,
  periodoProducaoAtual,
  TipoDesempenho,
  TIPO_DESEMPENHO_DEFAULTS,
  TIPO_DESEMPENHO_LABELS,
  tipoUsaFaixas,
} from '../types/desempenho';

interface TenantOption {
  id: number;
  nome: string;
  tipo_unidade?: 'MATRIZ' | 'ASSOCIADO' | 'REPRESENTACAO' | null;
}

interface Props {
  tenants?: TenantOption[];
  onRefresh?: () => void;
  allowCargoEdit?: boolean;
  allowCommissionEdit?: boolean;
}

interface PlanilhaRow {
  key: string;
  configId?: string;
  tipo: TipoDesempenho;
  profileId?: string;
  nome: string;
  personalizado: boolean;
  ativo: boolean;
  comissaoBase: number;
  comissaoPrimeira?: number;
  comissaoParcelas?: number;
  comissaoGerentePos?: number;
  metaValor?: number;
  faixas: DesempenhoFaixaFormData[];
  children?: PlanilhaRow[];
}

const NIVEIS = ['DIAMANTE', 'OURO', 'PLATINUM', 'BRONZE'] as const;

const ROLES_POR_TENANT: Record<string, TipoDesempenho[]> = {
  MATRIZ: ['SUPERVISOR', 'RELACIONADOR', 'LIDER_VENDAS', 'POS_VENDA'],
  REPRESENTACAO: ['REPRESENTANTE', 'SUPERVISOR', 'RELACIONADOR', 'LIDER_VENDAS', 'POS_VENDA'],
  ASSOCIADO: ['ASSOCIADO'],
};

const ROLE_PROFILE_ALIASES: Record<TipoDesempenho, string[]> = {
  SUPERVISOR: ['SUPERVISOR'],
  RELACIONADOR: ['RELACIONADOR', 'USER'],
  LIDER_VENDAS: ['LIDER_VENDAS', 'LIDER'],
  POS_VENDA: ['POS_VENDA'],
  REPRESENTANTE: ['REPRESENTANTE'],
  ASSOCIADO: ['ASSOCIADO'],
};

function grupoFromTenant(tipo?: TenantOption['tipo_unidade']): GrupoDesempenho {
  if (tipo === 'ASSOCIADO') return 'associado';
  if (tipo === 'REPRESENTACAO') return 'representacao';
  return 'time_interno';
}

function periodoValue(mes: number, ano: number) {
  return `${ano}-${String(mes).padStart(2, '0')}`;
}

function parsePeriodo(value: string) {
  const [ano, mes] = value.split('-').map(Number);
  return { periodo_mes: mes, periodo_ano: ano };
}

function periodoAnterior(value: string) {
  const { periodo_mes, periodo_ano } = parsePeriodo(value);
  return periodo_mes === 1
    ? periodoValue(12, periodo_ano - 1)
    : periodoValue(periodo_mes - 1, periodo_ano);
}

function formatInteiroBr(value?: string | number) {
  if (value === undefined || value === null || value === '') return '';
  return Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
}

function formatDecimalBr(value?: string | number) {
  if (value === undefined || value === null || value === '') return '';
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function parseMoedaBr(value?: string) {
  if (!value) return 0;
  return Number(value.replace(/[^\d,-]/g, '').replace(/\./g, '').replace(',', '.'));
}

function parseDecimalBr(value?: string) {
  if (!value) return 0;
  const sanitized = value.replace(/[^\d,.-]/g, '');
  return Number(
    sanitized.includes(',')
      ? sanitized.replace(/\./g, '').replace(',', '.')
      : sanitized
  );
}

function cloneFaixas(tipo: TipoDesempenho, faixas?: DesempenhoFaixa[]) {
  if (tipo === 'ASSOCIADO') return [];
  const origem = faixas?.length ? faixas : FAIXAS_DEFAULT_POR_TIPO[tipo];
  return origem.map(faixa => ({
    nivel: faixa.nivel,
    classificacao: faixa.classificacao,
    ordem: faixa.ordem,
    meta_valor: faixa.meta_valor,
    meta_reunioes: faixa.meta_reunioes,
    meta_fixacao: faixa.meta_fixacao,
    bonus_percentual: faixa.bonus_percentual,
  }));
}

function inheritValues(target: PlanilhaRow, source: PlanilhaRow): PlanilhaRow {
  return {
    ...target,
    ativo: source.ativo,
    comissaoBase: source.comissaoBase,
    comissaoPrimeira: source.comissaoPrimeira,
    comissaoParcelas: source.comissaoParcelas,
    comissaoGerentePos: source.comissaoGerentePos,
    metaValor: source.metaValor,
    faixas: source.faixas.map(faixa => ({ ...faixa })),
  };
}

export function DesempenhoMetasSpreadsheet({
  tenants = [],
  onRefresh,
  allowCargoEdit = true,
  allowCommissionEdit = true,
}: Props) {
  const atual = periodoProducaoAtual();
  const [tenantId, setTenantId] = useState<number>();
  const [periodo, setPeriodo] = useState(periodoValue(atual.periodo_mes, atual.periodo_ano));
  const [configs, setConfigs] = useState<DesempenhoConfig[]>([]);
  const [faixasMap, setFaixasMap] = useState<Record<string, DesempenhoFaixa[]>>({});
  const [membros, setMembros] = useState<DesempenhoMembro[]>([]);
  const [rows, setRows] = useState<PlanilhaRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId && tenants.length) setTenantId(tenants[0].id);
  }, [tenantId, tenants]);

  const tenant = useMemo(() => tenants.find(item => item.id === tenantId), [tenantId, tenants]);
  const grupo = grupoFromTenant(tenant?.tipo_unidade);

  const carregar = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const [allConfigs, allMembros] = await Promise.all([
        desempenhoService.listar(),
        desempenhoService.listarMembros(tenantId),
      ]);
      const ids = allConfigs.filter(config => config.tenant_id === tenantId).map(config => config.id);
      const allFaixas = await desempenhoService.listarFaixasPorConfigs(ids);
      setConfigs(allConfigs);
      setMembros(allMembros);
      setFaixasMap(allFaixas);
    } catch (error: any) {
      message.error(error.message || 'Erro ao carregar a planilha de metas');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!tenantId || !tenant) return;
    const { periodo_mes, periodo_ano } = parsePeriodo(periodo);
    const tipos = ROLES_POR_TENANT[tenant.tipo_unidade || 'MATRIZ'] ?? ROLES_POR_TENANT.MATRIZ;
    const configPeriodo = configs.filter(
      config =>
        config.tenant_id === tenantId &&
        config.periodo_mes === periodo_mes &&
        config.periodo_ano === periodo_ano
    );

    const novasRows = tipos.map(tipo => {
      const configCargo = configPeriodo.find(
        config => config.tipo_desempenho === tipo && !config.profile_id
      );
      const defaults = TIPO_DESEMPENHO_DEFAULTS[tipo];
      const cargo: PlanilhaRow = {
        key: `cargo-${tipo}`,
        configId: configCargo?.id,
        tipo,
        nome: TIPO_DESEMPENHO_LABELS[tipo],
        personalizado: true,
        ativo: configCargo?.ativo ?? true,
        comissaoBase: configCargo?.comissao_percentual_base ?? defaults.comissao_percentual_base ?? 0,
        comissaoPrimeira:
          configCargo?.comissao_primeira_parcela_percentual ??
          defaults.comissao_primeira_parcela_percentual,
        comissaoParcelas:
          configCargo?.comissao_parcelas_2_a_5_percentual ??
          defaults.comissao_parcelas_2_a_5_percentual,
        comissaoGerentePos:
          configCargo?.comissao_gerente_pos_percentual ??
          defaults.comissao_gerente_pos_percentual,
        metaValor: configCargo?.meta_valor ?? defaults.meta_valor,
        faixas: cloneFaixas(tipo, configCargo ? faixasMap[configCargo.id] : undefined),
      };

      cargo.children = membros
        .filter(
          membro =>
            membro.status !== 'INACTIVE' &&
            ROLE_PROFILE_ALIASES[tipo].includes(membro.role.toUpperCase())
        )
        .map(membro => {
          const individual = configPeriodo.find(
            config => config.tipo_desempenho === tipo && config.profile_id === membro.id
          );
          const base: PlanilhaRow = {
            key: `pessoa-${membro.id}-${tipo}`,
            configId: individual?.id,
            tipo,
            profileId: membro.id,
            nome: membro.nome,
            personalizado: Boolean(individual),
            ativo: individual?.ativo ?? cargo.ativo,
            comissaoBase: individual?.comissao_percentual_base ?? cargo.comissaoBase,
            comissaoPrimeira:
              individual?.comissao_primeira_parcela_percentual ?? cargo.comissaoPrimeira,
            comissaoParcelas:
              individual?.comissao_parcelas_2_a_5_percentual ?? cargo.comissaoParcelas,
            comissaoGerentePos:
              individual?.comissao_gerente_pos_percentual ?? cargo.comissaoGerentePos,
            metaValor: individual?.meta_valor ?? cargo.metaValor,
            faixas: individual
              ? cloneFaixas(tipo, faixasMap[individual.id])
              : cargo.faixas.map(faixa => ({ ...faixa })),
          };
          return base;
        });
      return cargo;
    });
    setRows(novasRows);
  }, [configs, faixasMap, membros, periodo, tenant, tenantId]);

  const updateRow = (key: string, updater: (row: PlanilhaRow) => PlanilhaRow) => {
    setRows(current =>
      current.map(cargo => {
        if (cargo.key === key) {
          const updated = updater(cargo);
          return {
            ...updated,
            children: updated.children?.map(child =>
              child.personalizado ? child : inheritValues(child, updated)
            ),
          };
        }
        return {
          ...cargo,
          children: cargo.children?.map(child => (child.key === key ? updater(child) : child)),
        };
      })
    );
  };

  const togglePersonalizado = (key: string, checked: boolean) => {
    setRows(current =>
      current.map(cargo => ({
        ...cargo,
        children: cargo.children?.map(child =>
          child.key === key
            ? checked
              ? { ...inheritValues(child, cargo), personalizado: true }
              : { ...inheritValues(child, cargo), personalizado: false }
            : child
        ),
      }))
    );
  };

  const updateFaixa = (
    row: PlanilhaRow,
    nivel: (typeof NIVEIS)[number],
    field: keyof DesempenhoFaixaFormData,
    value: number | null
  ) => {
    updateRow(row.key, current => ({
      ...current,
      faixas: current.faixas.map(faixa =>
        faixa.nivel === nivel ? { ...faixa, [field]: value ?? undefined } : faixa
      ),
    }));
  };

  const copiarAnterior = () => {
    const origem = periodoAnterior(periodo);
    const { periodo_mes, periodo_ano } = parsePeriodo(origem);
    const configsOrigem = configs.filter(
      config =>
        config.tenant_id === tenantId &&
        config.periodo_mes === periodo_mes &&
        config.periodo_ano === periodo_ano
    );
    if (!configsOrigem.length) {
      message.info('Não há metas cadastradas no período anterior.');
      return;
    }

    const aplicarConfig = (row: PlanilhaRow, config: DesempenhoConfig): PlanilhaRow => ({
      ...row,
      ativo: config.ativo,
      comissaoBase: config.comissao_percentual_base,
      comissaoPrimeira: config.comissao_primeira_parcela_percentual,
      comissaoParcelas: config.comissao_parcelas_2_a_5_percentual,
      comissaoGerentePos: config.comissao_gerente_pos_percentual,
      metaValor: config.meta_valor,
      faixas: cloneFaixas(row.tipo, faixasMap[config.id]),
    });

    setRows(current =>
      current.map(cargo => {
        const cargoOrigem = configsOrigem.find(
          config => config.tipo_desempenho === cargo.tipo && !config.profile_id
        );
        if (!cargoOrigem) return cargo;

        const cargoCopiado = allowCargoEdit ? aplicarConfig(cargo, cargoOrigem) : cargo;
        return {
          ...cargoCopiado,
          children: cargo.children?.map(child => {
            const individualOrigem = configsOrigem.find(
              config =>
                config.tipo_desempenho === child.tipo && config.profile_id === child.profileId
            );
            return individualOrigem
              ? { ...aplicarConfig(child, individualOrigem), personalizado: true }
              : { ...inheritValues(child, cargoCopiado), personalizado: false };
          }),
        };
      })
    );
    message.success(
      allowCargoEdit
        ? 'Metas do mês anterior copiadas. Revise e salve o período atual.'
        : 'Ajustes individuais do mês anterior copiados. Revise antes de salvar.'
    );
  };

  const salvar = async () => {
    if (!tenantId) return;
    const parsed = parsePeriodo(periodo);
    try {
      setSaving(true);
      for (const cargo of rows) {
        const candidatas = [
          ...(allowCargoEdit ? [cargo] : []),
          ...(cargo.children ?? []).filter(child => child.personalizado),
        ];
        const removidas = (cargo.children ?? []).filter(
          child => !child.personalizado && child.configId
        );

        for (const removida of removidas) await desempenhoService.deletar(removida.configId!);

        for (const row of candidatas) {
          const form: DesempenhoConfigFormData = {
            profile_id: row.profileId ?? null,
            grupo,
            tipo_desempenho: row.tipo,
            meta_valor: row.metaValor,
            comissao_percentual_base: row.comissaoBase,
            comissao_primeira_parcela_percentual: row.comissaoPrimeira,
            comissao_parcelas_2_a_5_percentual: row.comissaoParcelas,
            comissao_gerente_pos_percentual: row.comissaoGerentePos,
            periodo_mes: parsed.periodo_mes,
            periodo_ano: parsed.periodo_ano,
            ativo: row.ativo,
          };
          if (row.configId) {
            await desempenhoService.atualizarComFaixas(
              row.configId,
              form,
              row.faixas,
              row.tipo
            );
          } else {
            await desempenhoService.criarComFaixas(form, tenantId, row.faixas);
          }
        }
      }
      message.success('Metas salvas com sucesso.');
      await carregar();
      onRefresh?.();
    } catch (error: any) {
      message.error(error.message || 'Erro ao salvar as metas');
    } finally {
      setSaving(false);
    }
  };

  const metricInput = (
    row: PlanilhaRow,
    nivel: (typeof NIVEIS)[number],
    field: keyof DesempenhoFaixaFormData
  ) => {
    const faixa = row.faixas.find(item => item.nivel === nivel);
    const disabled =
      (row.profileId ? !row.personalizado : !allowCargoEdit) ||
      (field === 'bonus_percentual' && !allowCommissionEdit);
    const isMoney = field === 'meta_valor';
    const isPercent = field === 'meta_fixacao' || field === 'bonus_percentual';
    return (
      <InputNumber
        size="small"
        value={(faixa?.[field] as number | undefined) ?? null}
        onChange={value => updateFaixa(row, nivel, field, value)}
        disabled={disabled}
        min={0}
        precision={field === 'meta_reunioes' || isMoney ? 0 : 2}
        prefix={isMoney ? 'R$' : undefined}
        suffix={isPercent ? '%' : undefined}
        formatter={isMoney ? formatInteiroBr : formatDecimalBr}
        parser={isMoney ? parseMoedaBr : parseDecimalBr}
        controls={false}
        placeholder="0"
        className="w-full"
      />
    );
  };

  const columns: any[] = [
    {
      title: 'Cargo / membro',
      dataIndex: 'nome',
      fixed: 'left',
      width: 220,
      render: (_: unknown, row: PlanilhaRow) => (
        <Space>
          {row.profileId ? <UserRound size={15} /> : <UsersRound size={16} />}
          <div>
            <div className="font-medium">{row.nome}</div>
            <div className="text-xs text-gray-500">
              {row.profileId ? 'Meta individual' : 'Padrão do cargo'}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Aplicação',
      width: 145,
      render: (_: unknown, row: PlanilhaRow) =>
        row.profileId ? (
          <Checkbox
            checked={row.personalizado}
            onChange={event => togglePersonalizado(row.key, event.target.checked)}
          >
            Personalizar
          </Checkbox>
        ) : (
          <Tag color="blue">Padrão</Tag>
        ),
    },
    {
      title: 'Ativo',
      width: 75,
      align: 'center',
      render: (_: unknown, row: PlanilhaRow) => (
        <Checkbox
          checked={row.ativo}
          disabled={row.profileId ? !row.personalizado : !allowCargoEdit}
          onChange={event => updateRow(row.key, item => ({ ...item, ativo: event.target.checked }))}
        />
      ),
    },
    {
      title: 'Comissão',
      width: 90,
      render: (_: unknown, row: PlanilhaRow) => (
        <InputNumber
          size="small"
          value={row.comissaoBase}
          min={0}
          max={100}
          precision={2}
          suffix="%"
          formatter={formatDecimalBr}
          parser={parseDecimalBr}
          controls={false}
          disabled={
            (row.profileId ? !row.personalizado : !allowCargoEdit) || !allowCommissionEdit
          }
          onChange={value =>
            updateRow(row.key, item => ({ ...item, comissaoBase: Number(value ?? 0) }))
          }
          className="w-full"
        />
      ),
    },
    {
      title: 'Regras especiais',
      width: 135,
      render: (_: unknown, row: PlanilhaRow) => {
        const disabled =
          (row.profileId ? !row.personalizado : !allowCargoEdit) || !allowCommissionEdit;
        if (row.tipo === 'REPRESENTANTE') {
          return (
            <Space direction="vertical" size={4} className="w-full">
              <InputNumber
                size="small"
                addonBefore="1ª"
                suffix="%"
                value={row.comissaoPrimeira}
                min={0}
                precision={2}
                controls={false}
                formatter={formatDecimalBr}
                parser={parseDecimalBr}
                disabled={disabled}
                onChange={value =>
                  updateRow(row.key, item => ({
                    ...item,
                    comissaoPrimeira: Number(value ?? 0),
                  }))
                }
                className="w-full"
              />
              <InputNumber
                size="small"
                addonBefore={<span className="whitespace-nowrap">2ª–5ª</span>}
                suffix="%"
                value={row.comissaoParcelas}
                min={0}
                precision={2}
                controls={false}
                formatter={formatDecimalBr}
                parser={parseDecimalBr}
                disabled={disabled}
                onChange={value =>
                  updateRow(row.key, item => ({
                    ...item,
                    comissaoParcelas: Number(value ?? 0),
                  }))
                }
                className="w-full"
              />
            </Space>
          );
        }
        if (row.tipo === 'POS_VENDA') {
          return (
            <InputNumber
              size="small"
              addonBefore="Gerente"
              suffix="%"
              value={row.comissaoGerentePos}
              min={0}
              precision={2}
              controls={false}
              formatter={formatDecimalBr}
              parser={parseDecimalBr}
              disabled={disabled}
              onChange={value =>
                updateRow(row.key, item => ({
                  ...item,
                  comissaoGerentePos: Number(value ?? 0),
                }))
              }
              className="w-full"
            />
          );
        }
        return '—';
      },
    },
    ...NIVEIS.map(nivel => ({
      title: (
        <Tag
          color={{
            DIAMANTE: 'cyan',
            OURO: 'gold',
            PLATINUM: 'geekblue',
            BRONZE: 'orange',
          }[nivel]}
        >
          {NIVEL_DESEMPENHO_LABELS[nivel]}
        </Tag>
      ),
      width: 210,
      render: (_: unknown, row: PlanilhaRow) => {
        const field = (
          label: string,
          input: ReactNode
        ) => (
          <div className="grid grid-cols-[68px_1fr] items-center gap-2">
            <span className="text-[11px] text-gray-500 dark:text-zinc-400">{label}</span>
            {input}
          </div>
        );

        if (!tipoUsaFaixas(row.tipo)) {
          return nivel === 'DIAMANTE' ? (
            field(
              'Meta mensal',
              <InputNumber
                size="small"
                value={row.metaValor}
                prefix="R$"
                precision={0}
                formatter={formatInteiroBr}
                parser={parseMoedaBr}
                controls={false}
                disabled={row.profileId ? !row.personalizado : !allowCargoEdit}
                onChange={value =>
                  updateRow(row.key, item => ({ ...item, metaValor: Number(value ?? 0) }))
                }
                className="w-full"
              />
            )
          ) : (
            <span className="text-gray-400">Não se aplica</span>
          );
        }

        const campos = CAMPOS_META_POR_TIPO[row.tipo];
        return (
          <Space direction="vertical" size={5} className="w-full">
            {campos.valor && field('Faturamento', metricInput(row, nivel, 'meta_valor'))}
            {campos.fixacao && field('Fixação', metricInput(row, nivel, 'meta_fixacao'))}
            {campos.reunioes && field('Reuniões', metricInput(row, nivel, 'meta_reunioes'))}
            {field('Bônus', metricInput(row, nivel, 'bonus_percentual'))}
          </Space>
        );
      },
    })),
  ];

  const periodos = useMemo(() => {
    const options = [];
    for (let offset = -12; offset <= 12; offset += 1) {
      const date = new Date(atual.periodo_ano, atual.periodo_mes - 1 + offset, 1);
      const value = periodoValue(date.getMonth() + 1, date.getFullYear());
      options.push({
        value,
        label: date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
      });
    }
    return options.reverse();
  }, [atual.periodo_ano, atual.periodo_mes]);

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <Space wrap size="middle">
            <div>
              <Typography.Text type="secondary">Empresa</Typography.Text>
              <Select
                value={tenantId}
                onChange={setTenantId}
                options={tenants.map(item => ({ value: item.id, label: item.nome }))}
                className="desempenho-filter-select desempenho-filter-select--tenant"
                showSearch
                optionFilterProp="label"
              />
            </div>
            <div>
              <Typography.Text type="secondary">Período de produção</Typography.Text>
              <Select
                value={periodo}
                onChange={setPeriodo}
                options={periodos}
                className="desempenho-filter-select desempenho-filter-select--periodo"
              />
            </div>
          </Space>
          <Space>
            <Button icon={<Copy size={15} />} onClick={copiarAnterior}>
              Copiar mês anterior
            </Button>
            <Button
              type="primary"
              icon={<Save size={16} />}
              loading={saving}
              onClick={salvar}
            >
              {allowCargoEdit ? 'Salvar' : 'Salvar ajustes'}
            </Button>
          </Space>
        </div>
      </Card>

      <Alert
        type="info"
        showIcon
        icon={<Sheet size={18} />}
        title={allowCargoEdit ? 'Metas por cargo com ajustes individuais' : 'Metas do time'}
        description={
          allowCargoEdit
            ? 'Edite o padrão do cargo na linha principal. Expanda a linha e marque Personalizar somente para membros que precisam de uma meta diferente. Comissão fixa e bônus permanecem separados.'
            : 'As linhas de cargo mostram o padrão definido para a empresa. Expanda um cargo e marque Personalizar para ajustar somente um membro do time. Comissão e bônus são somente leitura.'
        }
      />

      <Card styles={{ body: { padding: 0 } }}>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          size="small"
          bordered
          scroll={{ x: 1500 }}
          expandable={{ defaultExpandAllRows: false }}
        />
      </Card>
    </div>
  );
}
